/**
 * `entities/project-member` API surface.
 *
 * Exposes:
 *   - Service functions for READING project members + workspace users.
 *   - `*Query` queryOptions helpers, so reads flow through queryOptions and
 *     honour the cache-key contract.
 *
 * Mutations (`updateProjectMembers`) live in
 * `features/manage-project-members/`.
 *
 * Migrated from `features/projects/api/projectMembers.api.ts`.
 */
import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { PlatformApi } from '@/lib/api/platform/platformSdk';
import { workspaceHttp } from '@/lib/api/http-client';
import type {
  AssignableProjectRole,
  ProjectMembersResponse,
  RoleAssignment,
  WorkspaceUsersResponse,
} from '../model/types';
import { PROJECT_MEMBER_QUERY_KEYS } from './query-keys';

// ──────────────────────────── Service functions ────────────────────────────

export const getProjectMembers = async (
  workspaceId: string,
  platformProjectId: string,
): Promise<ProjectMembersResponse> => {
  const response = await PlatformApi.platformApi.get<ProjectMembersResponse>(
    `/workspaces/${workspaceId}/projects/${platformProjectId}/users`,
  );
  return response.data;
};

export const getWorkspaceUsers = async (
  workspaceId: string,
  name?: string,
): Promise<WorkspaceUsersResponse> => {
  const params = new URLSearchParams();
  if (name) {
    params.set('name', name);
  }

  const query = params.toString();
  const url = `/workspaces/${workspaceId}/users${query ? `?${query}` : ''}`;

  const response = await PlatformApi.platformApi.get<WorkspaceUsersResponse>(url);
  return response.data;
};

// ──────────────────────────── queryOptions helpers ─────────────────────────

export const projectMembersQuery = (
  workspaceId: string | null | undefined,
  platformProjectId: string | null | undefined,
) =>
  queryOptions({
    queryKey: PROJECT_MEMBER_QUERY_KEYS.byProject(workspaceId!, platformProjectId!),
    queryFn: () => getProjectMembers(workspaceId!, platformProjectId!),
    enabled: !!workspaceId && !!platformProjectId,
  });

// The Platform API rejects a `name` filter shorter than this with a 400.
// An empty term is allowed — it returns the unfiltered workspace roster.
const MIN_USER_SEARCH_LENGTH = 3;

export const workspaceUsersQuery = (workspaceId: string | null | undefined, name?: string) => {
  const term = name?.trim() ?? '';
  const hasSearchableTerm = term.length === 0 || term.length >= MIN_USER_SEARCH_LENGTH;

  return queryOptions({
    queryKey: PROJECT_MEMBER_QUERY_KEYS.workspaceUsers.search(workspaceId!, name),
    queryFn: () => getWorkspaceUsers(workspaceId!, name),
    // Skip 1–2 char terms entirely so we don't fire a request the backend
    // will reject. `keepPreviousData` then leaves the prior results on screen.
    enabled: !!workspaceId && hasSearchableTerm,
    // The search term is part of the query key, so each keystroke spawns a
    // fresh query. Keep the previous results visible during the refetch
    // instead of dropping to an empty `isLoading` state — this stops the
    // search field from being disabled (and blurred) mid-typing.
    placeholderData: keepPreviousData,
  });
};

// ──────────────────────────── Role service functions ─────────────────────────

export const getProjectAssignableRoles = async (
  platformProjectId: string,
): Promise<AssignableProjectRole[]> => {
  const response = await workspaceHttp.get({
    url: '/api/v1/projects/{platformProjectId}/assignable-roles',
    path: { platformProjectId },
  });
  const body = response.data as { data: AssignableProjectRole[] } | undefined;
  return body?.data ?? [];
};

export const getProjectRoleAssignments = async (
  platformProjectId: string,
): Promise<RoleAssignment[]> => {
  const response = await workspaceHttp.get({
    url: '/api/v1/projects/{platformProjectId}/role-assignments',
    path: { platformProjectId },
  });
  const body = response.data as { data: RoleAssignment[] } | undefined;
  return body?.data ?? [];
};

// ──────────────────────────── Role queryOptions ─────────────────────────────

export const projectAssignableRolesQuery = (platformProjectId: string | null | undefined) =>
  queryOptions({
    queryKey: PROJECT_MEMBER_QUERY_KEYS.assignableRoles(platformProjectId!),
    queryFn: () => getProjectAssignableRoles(platformProjectId!),
    enabled: !!platformProjectId,
    staleTime: 5 * 60 * 1000,
  });

export const projectRoleAssignmentsQuery = (platformProjectId: string | null | undefined) =>
  queryOptions({
    queryKey: PROJECT_MEMBER_QUERY_KEYS.roleAssignments(platformProjectId!),
    queryFn: () => getProjectRoleAssignments(platformProjectId!),
    enabled: !!platformProjectId,
  });
